import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { ArrowLeft, Clock, Heart, Wind, Sparkles } from "lucide-react";

// App name constant
const APP_NAME = "the soft ritual";
import { API } from "../App";

export default function SessionHistory({ auth }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState(null);

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      const response = await fetch(`${API}/sessions?limit=20`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setSessions(data);
      }
    } catch (error) {
      console.error('Error fetching sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long',
      month: 'long', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const formatDateShort = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-center animate-fade-in">
          <div className="w-12 h-12 border-2 border-[#A87C6D] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#6E6A66] font-light">Loading your reflections...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="session-history">
      {/* Navigation */}
      <nav className="px-6 py-4 border-b border-[#E6E2DD]">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <Link to="/dashboard">
            <Button variant="ghost" className="rounded-full text-[#6E6A66] hover:text-[#2D2A26] hover:bg-[#E6E2DD]">
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </Button>
          </Link>
          <span 
            className="text-xl font-light text-[#2D2A26]"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            Your reflections
          </span>
          <div className="w-20"></div>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-2xl mx-auto px-6 py-12">
        {sessions.length === 0 ? (
          <div className="text-center py-16 animate-fade-in">
            <div className="w-16 h-16 rounded-full bg-[#E6E2DD] flex items-center justify-center mx-auto mb-6">
              <Clock className="w-8 h-8 text-[#A8A4A0]" />
            </div>
            <h2 
              className="text-2xl font-normal text-[#2D2A26] mb-3"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              No reflections yet
            </h2>
            <p className="text-[#6E6A66] font-light mb-8">
              Your check-in sessions will appear here.
            </p>
            <Link to="/check-in">
              <Button className="rounded-full px-8 py-5 bg-[#A87C6D] hover:bg-[#956d5f] text-white">
                Begin your first check-in
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-4 animate-fade-in">
            {sessions.map((session, idx) => (
              <div 
                key={session.session_id}
                className="paper-card rounded-2xl p-6 hover-lift cursor-pointer transition-all duration-300"
                onClick={() => setSelectedSession(session)}
                data-testid={`history-session-${idx}`}
                style={{ 
                  animationDelay: `${idx * 50}ms`,
                  opacity: 0,
                  animation: 'slideUp 500ms ease-out forwards'
                }}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2 text-sm text-[#A8A4A0]">
                    <Clock className="w-4 h-4" />
                    <span>{formatDateShort(session.created_at)}</span>
                  </div>
                  {session.emotional_themes?.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {session.emotional_themes.slice(0, 2).map((theme, i) => (
                        <span 
                          key={i}
                          className="px-2 py-1 rounded-full bg-[#A87C6D]/10 text-[#A87C6D] text-xs"
                        >
                          {theme}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <p className="text-[#2D2A26] font-light line-clamp-3 leading-relaxed">
                  {session.user_message?.substring(0, 200)}...
                </p>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Session Detail Modal */}
      <Dialog open={!!selectedSession} onOpenChange={() => setSelectedSession(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[#FDFBF7] border-[#E6E2DD]">
          <DialogHeader>
            <DialogTitle 
              className="text-xl font-normal text-[#2D2A26]"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              {selectedSession && formatDate(selectedSession.created_at)}
            </DialogTitle>
          </DialogHeader>
          
          {selectedSession && (
            <div className="space-y-6 py-4">
              {/* User's message */}
              <div className="paper-card rounded-2xl p-4">
                <p className="text-sm text-[#A8A4A0] mb-2">You shared:</p>
                <p className="text-[#2D2A26] font-light leading-relaxed whitespace-pre-line">
                  {selectedSession.user_message}
                </p>
              </div>

              {/* AI Response */}
              {selectedSession.ai_response?.sections && (
                <>
                  {selectedSession.ai_response.sections.reflect && (
                    <div className="ai-section reflect">
                      <div className="flex items-center gap-2 mb-2">
                        <Heart className="w-4 h-4 text-[#A87C6D]" />
                        <span className="text-sm font-medium text-[#A87C6D]">Reflect</span>
                      </div>
                      <p className="text-[#2D2A26] font-light leading-relaxed whitespace-pre-line">
                        {selectedSession.ai_response.sections.reflect}
                      </p>
                    </div>
                  )}

                  {selectedSession.ai_response.sections.regulate && (
                    <div className="ai-section regulate">
                      <div className="flex items-center gap-2 mb-2">
                        <Wind className="w-4 h-4 text-[#7A9E7E]" />
                        <span className="text-sm font-medium text-[#7A9E7E]">Regulate</span>
                      </div>
                      <p className="text-[#2D2A26] font-light leading-relaxed whitespace-pre-line">
                        {selectedSession.ai_response.sections.regulate}
                      </p>
                    </div>
                  )}

                  {selectedSession.ai_response.sections.redirect && (
                    <div className="ai-section redirect">
                      <div className="flex items-center gap-2 mb-2">
                        <Sparkles className="w-4 h-4 text-[#7D98A1]" />
                        <span className="text-sm font-medium text-[#7D98A1]">Redirect</span>
                      </div>
                      <p className="text-[#2D2A26] font-light leading-relaxed whitespace-pre-line">
                        {selectedSession.ai_response.sections.redirect}
                      </p>
                    </div>
                  )}
                </>
              )}

              {/* Emotional themes */}
              {selectedSession.emotional_themes?.length > 0 && (
                <div className="pt-4 border-t border-[#E6E2DD]">
                  <p className="text-sm text-[#A8A4A0] mb-2">Themes from this session:</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedSession.emotional_themes.map((theme, i) => (
                      <span 
                        key={i}
                        className="px-3 py-1 rounded-full bg-[#E6E2DD] text-[#6E6A66] text-sm"
                      >
                        {theme}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
